import { Usage } from "../flatbuffers/generated/usage/usage";

/**
 * Typed usage map keyed by the Usage enum.
 *
 * Each entry records whether a particular usage signal is active (true)
 * or explicitly inactive (false). Absent keys mean the signal was never
 * received.
 */
export type UsageMap = Partial<Record<Usage, boolean>>;

/** Elevator icon names produced by the icon-resolution functions. */
export enum ElevatorIcon {
  Up = "elUp",
  Down = "elDown",
  Open = "elOpen",
  Close = "elClose",
  Opening = "elOpening",
  RearOpen = "elRearOpen",
  RearClose = "elRearClose",
  RearOpening = "elRearOpening",
  FrontOpenRearOpen = "elFrontOpenRearOpen",
  FrontOpenRearClose = "elFrontOpenRearClose",
  FrontCloseRearOpen = "elFrontCloseRearOpen",
  FrontCloseRearClose = "elFrontCloseRearClose",
  FrontOpeningRearOpen = "elFrontOpeningRearOpen",
  FrontOpeningRearClose = "elFrontOpeningRearClose",
  FrontOpenRearOpening = "elFrontOpenRearOpening",
  FrontCloseRearOpening = "elFrontCloseRearOpening",
  FrontOpeningRearOpening = "elFrontOpeningRearOpening",
  Warning = "elWarning",
}

/** Escalator icon names. */
export enum EscalatorIcon {
  Up = "esUp",
  Down = "esDown",
  Stopped = "esStopped",
}

/** Control panel tab identifiers. */
export enum ControlPanelTab {
  CARCALLS = "CARCALLS",
  HALLCALLS = "HALLCALLS",
  ACTIONS = "ACTIONS",
  FAULTS = "FAULTS",
  STATUS = "STATUS",
  STATE = "STATE",
}

/** Device type identifiers. */
export enum DeviceType {
  ELEVATOR = "ELEVATOR",
  ESCALATOR = "ESCALATOR",
  MOVINGWALK = "MOVINGWALK",
}

/**
 * Door-related Usage codes that constitute "door data".
 * Used to determine whether any door signals have been received.
 */
export const DOOR_USAGE_CODES: readonly Usage[] = [
  Usage.FrontDoorOpened,
  Usage.FrontDoorClosed,
  Usage.FrontDoorOpening,
  Usage.FrontDoorClosing,
  Usage.RearDoorOpened,
  Usage.RearDoorClosed,
  Usage.RearDoorOpening,
  Usage.RearDoorClosing,
];

/**
 * Usage codes that report a direction of travel. Presence of any in the
 * UsageMap indicates that direction data has been received from the
 * controller (see ELEVATOR_IDLE_USAGE_CODES for the other half).
 */
export const DIRECTION_USAGE_CODES: readonly Usage[] = [
  Usage.Up,
  Usage.Down,
  Usage.Forward,
  Usage.Backward,
  Usage.Moving,
  Usage.RunUp,
  Usage.RunDown,
];

/**
 * Usage codes that report an elevator is not travelling. A car reporting that
 * it is parked or stopped IS reporting its direction — it has none — so these
 * count as direction data just as the travel codes do. Leaving them out made a
 * car whose vendor reports Parked + NormalStopped show "Awaiting: direction"
 * until it first moved.
 */
export const ELEVATOR_IDLE_USAGE_CODES: readonly Usage[] = [
  Usage.Parked,
  Usage.NormalStopped,
  Usage.FaultedStopped,
];
